import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  ChartBarIcon, 
  Cog6ToothIcon, 
  CalendarIcon,
  HeartIcon,
  UserGroupIcon,
  PlusCircleIcon,
  ChartPieIcon,
  StarIcon,
  ChevronDownIcon,
  UserIcon,
  QuestionMarkCircleIcon,
  EnvelopeIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { useAppTheme } from '@/context/ThemeContext';
import { useState, useEffect } from 'react';

type NavItem = {
  name: string;
  path: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
  parameter?: number;
  children?: NavItem[];
  permissions?: string[];
};

type DevCharitySidebarProps = {
  isOpen: boolean;
  isMinimized: boolean;
  onClose: () => void;
  theme?: string;
};

type NavItemProps = {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
  isMinimized: boolean;
};

// Dev Charity Navigation with correct paths
const devCharityNavigation: NavItem[] = [
  { name: 'Dashboard', path: '/dev/charity/dashboard', icon: HomeIcon, parameter: 1 },
  { 
    name: 'Donations', 
    path: '/dev/charity/donations', 
    icon: HeartIcon, 
    parameter: 2,
    children: [
      { name: 'All Donations', path: '/dev/charity/donations', icon: HeartIcon },
      { name: 'Request Donations', path: '/dev/charity/donations/request', icon: PlusCircleIcon }
    ]
  },
  { 
    name: 'Events', 
    path: '/dev/charity/events', 
    icon: CalendarIcon, 
    parameter: 3,
    children: [
      { name: 'All Events', path: '/dev/charity/events', icon: CalendarIcon },
      { name: 'Add Event', path: '/dev/charity/events/create', icon: PlusCircleIcon }
    ]
  },
  { name: 'Volunteers', path: '/dev/charity/volunteers', icon: UserGroupIcon, parameter: 4 },
  { 
    name: 'Staff Management', 
    path: '/dev/charity/users', 
    icon: UserGroupIcon, 
    parameter: 5 
  },
  { 
    name: 'Statistics', 
    path: '/dev/charity/statistics', 
    icon: ChartPieIcon, 
    parameter: 6, 
    children: [
      { name: 'Ratings & Reviews', path: '/dev/charity/statistics/ratings', icon: StarIcon },
      { name: 'Social Impact', path: '/dev/charity/statistics/social-impact', icon: HeartIcon },
      { name: 'Analytics', path: '/dev/charity/statistics/analytics', icon: ChartBarIcon }
    ]
  },
  { 
    name: 'Settings', 
    path: '/dev/charity/settings', 
    icon: Cog6ToothIcon, 
    parameter: 7
  },
  // Add these items to the devCharityNavigation array
{ 
  name: 'Profile', 
  path: '/dev/charity/profile', 
  icon: UserIcon, 
  parameter: 8
},
{ 
  name: 'Help Center', 
  path: '/dev/charity/help', 
  icon: QuestionMarkCircleIcon, 
  parameter: 9
},
{ 
  name: 'FAQ', 
  path: '/dev/charity/help/faq', 
  icon: QuestionMarkCircleIcon, 
  parameter: 10
},
{ 
  name: 'Contact Support', 
  path: '/dev/charity/help/contact-support', 
  icon: EnvelopeIcon, 
  parameter: 11
},
{ 
  name: 'Report Issue', 
  path: '/dev/charity/help/report-issue', 
  icon: ExclamationCircleIcon, 
  parameter: 12
}
];

// NavItem component with dropdown support
const NavItemComponent: React.FC<NavItemProps> = ({ item, isActive, onClick, isMinimized }) => {
  const { themeConfig } = useAppTheme();
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Determine if this item or any of its children is active
  const isItemActive = isActive || (item.children?.some(child => 
    location.pathname === child.path || 
    location.pathname.startsWith(`${child.path}/`)
  ) ?? false);

  // For items with children, toggle dropdown
  const handleClick = () => {
    if (item.children && item.children.length > 0) {
      setIsDropdownOpen(!isDropdownOpen);
    } else {
      onClick();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(`[data-item="${item.name}"]`)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [item.name]);

  return (
    <div 
      data-item={item.name}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* If item has children, render as a dropdown button */}
      {item.children && item.children.length > 0 ? (
        <div>
          <button
            onClick={handleClick}
            className={`w-full flex items-center px-3 py-2 rounded-md transition-colors duration-200 ${
              isMinimized ? 'justify-center' : 'justify-between'
            }`}
            style={{ 
              backgroundColor: isItemActive 
                ? themeConfig.accent
                : isHovered 
                  ? themeConfig.surface 
                  : 'transparent'
            }}
          >
            <div className="flex items-center">
              <item.icon 
                className="h-5 w-5 flex-shrink-0" 
                style={{ 
                  color: isItemActive 
                    ? '#0c4a8a' // Darker blue for active icon
                    : themeConfig.text
                }}
                aria-hidden="true" 
              />
              {!isMinimized && (
                <span 
                  className="ml-3 text-sm font-medium"
                  style={{ 
                    color: isItemActive 
                      ? '#0c4a8a' // Darker blue for active text
                      : themeConfig.text
                  }}
                >
                  {item.name}
                </span>
              )}
            </div>
            {!isMinimized && (
              <ChevronDownIcon 
                className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'transform rotate-180' : ''}`}
                style={{ color: themeConfig.text }}
              />
            )}
          </button>
          
          {/* Dropdown menu */}
          {(isDropdownOpen || (isMinimized && isHovered)) && (
            <div 
              className={`${
                isMinimized 
                  ? 'absolute left-full top-0 ml-2 w-48 z-50 rounded-md shadow-lg' 
                  : 'mt-1 rounded-md'
              } py-1 bg-white overflow-hidden`}
              style={{ 
                backgroundColor: themeConfig.background,
                boxShadow: isMinimized ? '0 2px 8px rgba(0,0,0,0.15)' : 'none'
              }}
            >
              {item.children.map(child => (
                <Link
                  key={child.name}
                  to={child.path} /* Use the path directly since it already has the /dev/ prefix */
                  onClick={() => onClick()}
                  className="flex items-center px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition-colors duration-200"
                  style={{ 
                    color: themeConfig.text,
                    backgroundColor: location.pathname === child.path ? themeConfig.accent : 'transparent',
                  }}
                >
                  {child.icon && (
                    <child.icon 
                      className="h-4 w-4 mr-2" 
                      style={{ 
                        color: location.pathname === child.path ? '#0c4a8a' : themeConfig.text
                      }} 
                    />
                  )}
                  <span 
                    className="text-sm"
                    style={{ 
                      color: location.pathname === child.path ? '#0c4a8a' : themeConfig.text
                    }}
                  >
                    {child.name}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      ) : (
        // If item has no children, render as a link
        <Link
          to={item.path} /* Use the path directly since it already has the /dev/ prefix */
          onClick={onClick}
          className={`w-full flex items-center px-3 py-2 rounded-md transition-colors duration-200 ${
            isMinimized ? 'justify-center' : ''
          }`}
          style={{
            backgroundColor: isItemActive 
              ? themeConfig.accent
              : isHovered 
                ? themeConfig.surface 
                : 'transparent'
          }}
        >
          <item.icon 
            className="h-5 w-5 flex-shrink-0" 
            style={{ 
              color: isItemActive 
                ? '#0c4a8a' // Darker blue for active icon
                : themeConfig.text
            }}
            aria-hidden="true" 
          />
          {!isMinimized && (
            <span 
              className="ml-3 text-sm font-medium"
              style={{ 
                color: isItemActive 
                  ? '#0c4a8a' // Darker blue for active text
                  : themeConfig.text
              }}
            >
              {item.name}
            </span>
          )}
        </Link>
      )}
    </div>
  );
};

export default function DevCharitySidebar({ isOpen, isMinimized, onClose }: DevCharitySidebarProps) {
  const location = useLocation();
  const { themeConfig } = useAppTheme();
  const [activeParameter, setActiveParameter] = useState<number>(1);
  
  const navigation = devCharityNavigation;

  // Set active parameter based on location
  useEffect(() => {
    const currentPath = location.pathname;
    const activeItem = navigation.find(item => {
      if (item.path === '/' && currentPath === '/') return true;
      if (item.path !== '/' && currentPath.startsWith(item.path)) return true;
      return false;
    });
    
    if (activeItem?.parameter) {
      setActiveParameter(activeItem.parameter);
    }
  }, [location.pathname, navigation]);

  // Handle navigation item click
  const handleNavItemClick = (item: NavItem) => {
    setActiveParameter(item.parameter || 1);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/30 lg:hidden" 
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar container */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isMinimized ? 'lg:w-16' : 'lg:w-[240px]'}
          w-[240px] p-2`}
      >
        <div className="flex flex-col h-full rounded-xl overflow-hidden shadow-md border border-opacity-30"
          style={{
            backgroundColor: themeConfig.background,
            borderColor: '#FFCC00', // Yellow color for dev sidebar border
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)'
          }}
        >
          {/* Logo section with dev indicator */}
          <div className="flex items-center justify-center h-16 px-3 bg-yellow-400">
            <div className="flex flex-col items-center">
              <img 
                src="/favicon.png" 
                alt="Logo" 
                className="h-8 w-auto"
              />
              {!isMinimized && (
                <span className="text-xs font-bold text-black mt-1">DEV MODE</span>
              )}
            </div>
          </div>
          
          {/* Divider */}
          <div className="h-[1px] w-full" style={{ backgroundColor: '#FFCC0090' }}></div>

          {/* Navigation section */}
          <div className="flex-1 overflow-y-auto py-3">
            {/* Navigation items */}
            <div className="space-y-0.5">
              {navigation.map((item) => (
                <NavItemComponent
                  key={item.name}
                  item={item}
                  isActive={activeParameter === item.parameter}
                  onClick={() => handleNavItemClick(item)}
                  isMinimized={isMinimized}
                />
              ))}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
